import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Slide, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CatalogMenu from './CatalogMenu';
import HoverLink from '../HoverLink';
import { useAppDispatch } from '../../store/hooks';
import { fetchCategories } from '../../store/slices/catalogSlice';
import { useAuth } from '../../contexts/AuthContext';

interface NavbarLinksProps {
  isSticky: boolean;
}

const NavbarLinks: React.FC<NavbarLinksProps> = ({ isSticky }) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const theme = useTheme();
  const linkColor = theme.palette.text.primary;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const catalogButtonRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setShowLinks(true);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClose = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsMenuOpen(false);
    };
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [isMenuOpen]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(fetchCategories());

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsMenuOpen(true);
    catalogButtonRef.current = event.currentTarget;
  };

  const handleMenuClose = (delay = 200) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, delay);
  };

  const navLinks = [
    ...(user ? [{ to: '/chinese', label: 'Китайский язык' }] : []),
    { to: '/company', label: 'Компания' },
    { to: '/contacts', label: 'Контакты' },
    { to: '/how-to-buy', label: 'Доставка и оплата' },
    { to: '/order-status', label: 'Статус заказа' },
    { to: 'https://yuzhen.store/blog/', label: 'Блог' },
  ];

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '20px',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      <Slide in={showLinks} direction="down" timeout={{ enter: 250 }} style={{ transitionDelay: '40ms' }}>
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            width: 150,
            height: 80,
            position: 'relative',
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          onMouseEnter={handleMenuOpen}
          onMouseLeave={() => handleMenuClose(100)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <Box sx={{ position: 'relative', width: '24px', height: '24px', mr: 1 }}>
              <MenuIcon
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: isMenuOpen ? 0 : 1,
                  transform: isMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'opacity 0.2s, transform 0.2s',
                  color: linkColor,
                }}
              />
              <MenuOpenIcon
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: isMenuOpen ? 1 : 0,
                  transform: isMenuOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'opacity 0.2s, transform 0.2s',
                  color: linkColor,
                }}
              />
            </Box>

            <Typography variant="h6">
              <HoverLink
                to="/catalog"
                label="Каталог"
                color={linkColor}
                underlineColor={linkColor}
                variant="h6"
              />
            </Typography>
          </Box>
        </Box>
      </Slide>

      <CatalogMenu
        anchorEl={catalogButtonRef.current}
        isOpen={isMenuOpen}
        onClose={() => {
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
          setIsMenuOpen(false);
        }}
        onMouseEnter={() => {
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => handleMenuClose(600)}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px 20px',
          height: '100%',
          flexWrap: 'wrap',
          maxWidth: '600px',
          justifyContent: 'center',
        }}
      >
        {navLinks.map((link, index) => (
          <Slide
            key={link.to}
            in={showLinks}
            direction="down"
            timeout={{ enter: 250 }}
            style={{ transitionDelay: `${60 + index * 60}ms` }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '32px',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
            >
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                <HoverLink
                  to={link.to}
                  label={link.label}
                  color={linkColor}
                  underlineColor={linkColor}
                  variant="h6"
                />
              </Typography>
            </Box>
          </Slide>
        ))}
      </Box>
    </Box>
  );
};

export default NavbarLinks;
